import type { Client, CrmData } from '../types/crm';
import { projectDetailPath } from './dashboardHelpers';
import { idsMatch } from './crmHelpers';
import { latestUpdatePreview } from './projectHelpers';

export interface AccountActivityItem {
  id: string;
  date: string;
  title: string;
  detail: string;
  tone: 'project' | 'contact' | 'task' | 'update' | 'document' | 'minutes' | 'transfer' | 'profile';
  href?: string;
}

export function buildAccountActivity(data: CrmData, client: Client): AccountActivityItem[] {
  const items: AccountActivityItem[] = [];
  const clientId = client.id;

  const projects = data.projects.filter((p) => idsMatch(p.client_id, clientId));

  for (const p of projects) {
    const href = projectDetailPath(p);
    items.push({
      id: `proj-${p.id}`,
      date: p.created_at ?? '',
      title: `New project · ${p.title}`,
      detail: `${p.reference_no ?? '—'} · ${p.status ?? 'Ongoing'}`,
      tone: 'project',
      href,
    });
  }

  for (const c of data.contacts) {
    if (!idsMatch(c.client_id, clientId)) continue;
    items.push({
      id: `contact-${c.id}`,
      date: c.created_at ?? '',
      title: `Contact added · ${c.first_name} ${c.last_name}`.trim(),
      detail: [c.job_title, c.email, c.phone].filter(Boolean).join(' · ') || '—',
      tone: 'contact',
    });
  }

  for (const m of data.meetingMinutes) {
    if (!idsMatch(m.client_id, clientId)) continue;
    const discussion = (m.discussion ?? m.body ?? '').trim();
    const posted = m.status === 'posted' || m.posted_at;
    items.push({
      id: `minutes-${m.id}`,
      date: m.posted_at ?? m.meeting_date ?? m.created_at ?? '',
      title: `Meeting minutes · ${m.title}${posted ? '' : ' (draft)'}`,
      detail: discussion.length > 120 ? `${discussion.slice(0, 120)}…` : discussion || 'Draft',
      tone: 'minutes',
    });
  }

  for (const t of data.tasks) {
    if (!idsMatch(t.client_id, clientId)) continue;
    items.push({
      id: `task-${t.id}`,
      date: t.created_at ?? t.due_date ?? '',
      title: `Task · ${t.title}`,
      detail: t.status ?? 'To Do',
      tone: 'task',
    });
  }

  for (const log of data.activityLogs) {
    if (!idsMatch(log.client_id, clientId)) continue;
    const tone =
      log.event_type === 'profile_updated' || log.event_type === 'account_created'
        ? 'profile'
        : 'transfer';
    items.push({
      id: `alog-${log.id}`,
      date: log.created_at ?? '',
      title: log.title,
      detail: log.detail,
      tone,
    });
  }

  for (const u of data.updates) {
    const proj = projects.find((p) => idsMatch(p.id, u.project_id));
    if (!proj) continue;

    const href = projectDetailPath(proj);

    let detail = latestUpdatePreview(u, 200);
    if (u.attachment_name) {
      detail = detail.includes('📎') ? detail : `${detail} · 📎 ${u.attachment_name}`;
    }

    items.push({
      id: `update-${u.id}`,
      date: u.created_at ?? '',
      title: `Project log · ${proj.title}`,
      detail,
      tone: 'update',
      href,
    });
  }

  return items
    .filter((a) => a.date)
    .sort((a, b) => b.date.localeCompare(a.date));
}

export function googleNewsSearchUrl(companyName: string): string {
  return `https://www.google.com/search?q=${encodeURIComponent(`${companyName} Singapore news`)}&tbm=nws`;
}

export function googleMapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}
